import { useProcess } from '@fasstech/oip-starter-utils/app';
import { WidePageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import { Icon, Message, PageSubtitle, PageTitle, SectionCard } from '@fasstech/middle-design-system';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer';
import NiveauxGarantiesTarifsPrevTnsGarantiesDecesForm
  from './components/NiveauxGarantiesTarifsPrevTnsGarantiesDecesForm';
import NiveauxGarantiesTarifsPrevTnsRenteEducationForm
  from './components/NiveauxGarantiesTarifsPrevTnsRenteEducationForm';
import NiveauxGarantiesTarifsPrevTnsRenteConjointForm
  from './components/NiveauxGarantiesTarifsPrevTnsRenteConjointForm';
import NiveauxGarantiesTarifsPrevTnsIncapaciteTemporaireDeTravailForm
  from './components/NiveauxGarantiesTarifsPrevTnsIncapaciteTemporaireDeTravailForm';
import NiveauxGarantiesTarifsPrevTnsInvaliditeForm from './components/NiveauxGarantiesTarifsPrevTnsInvaliditeForm';
import useSimulationPricingsSelfWorkerForm from './hooks/useSimulationPricingsSelfWorkerForm';
import useRulesEngine from './hooks/useRulesEngine';
import { FormProvider } from 'react-hook-form';
import { any, filter, includes, isEmpty, map, path } from 'ramda';
import { getNestedErrors } from './helpers/getNestedErrors.js';
import useExtraRules from './hooks/extra/useExtraRules.js';
import useRules from './hooks/useRules.js';
import { ControlledCommissionCard } from '@@viewCommonComponents/controlled';
import { isRequiredSelect } from '@@viewHelpers/validations/index.js';
import SimulationPricingsSelfWorkerExtraRecommendationMessages from './extra/SimulationPricingsSelfWorkerExtraRecommendationMessages';

const SimulationPricingsSelfWorkerForm = ({
  getSimulationPricingsSelfWorker,
  getMiddleText,
  getMiddleNumber,
  getMiddleBoolean,
  usersInformation
}) => {
  const {
    processHandlers,
    context
  } = useProcess();

  const $onClickPrev = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const commissions = getMiddleText('NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_VALEUR');

  const {
    form,
    onSubmit,
    guarantees,
    pricesGuarantees,
    pricesLoading,
    minBirthDateReducChild,
    maxBirthDateReducChild,
    minBirthDateHospiChild,
    maxBirthDateHospiChild,
    customerCSPCategory,
    displayPriceError,
    getCommissionRateOptions,
    getCommissionOptions,
    handleInvalDepCapFocus,
    handleInvalDepCapBlur,
    recommendationMessages,
    annualIncome,
    PASS,
    registerComputedFranchiseDataGetter
  } = useSimulationPricingsSelfWorkerForm({
    getSimulationPricingsSelfWorker,
    commissions
  });

  useRules({ form });
  const {
    onOptionFrMalChange,
    onOptionFrAccChange,
    onOptionFrHospChange
  } = useExtraRules({ form });
  useRulesEngine(form);

  const isValid = isEmpty(form.formState.errors);
  const errorMessages = getNestedErrors(form.formState.errors);

  const decesFieldKeys = ['TT_CAUSE_CAP', 'ACC_CAP', 'OPTION_REDUC', 'REDUC_CAP', 'OPTION_RCONJ', 'RCONJ_CAP'];
  const incapFieldKeys = ['OPTION_INCAP', 'OPTION_FR_MAL', 'OPTION_FR_ACC', 'OPTION_FR_HOSP', 'OPTION_FR_ALL', 'OPTION_INCAP_CAP', 'OPTION_HOSP_ENF_GAR', 'HOSP_ENF_ALLOW_AMOUNT', 'OPTION_FR_GEN', 'OPTION_FR_GEN_FR_MAL', 'OPTION_FR_GEN_FR_ACC', 'OPTION_FR_GEN_FR_HOSP', 'OPTION_FR_GEN_FR_TT_CAUSE', 'FR_GEN_CAP'];
  const invalFieldKeys = ['OPTION_INVAL', 'INVAL_CAP', 'INVAL_DEP_CAP'];

  const filterErrorsBySection = (errors, fieldKeys) =>
    filter(
      ({ fieldName }) =>
        any(key => includes(key, fieldName), fieldKeys),
      errors
    );

  const decesErrors = filterErrorsBySection(errorMessages, decesFieldKeys);
  const incapErrors = filterErrorsBySection(errorMessages, incapFieldKeys);
  const invalErrors = filterErrorsBySection(errorMessages, invalFieldKeys);

  const {
    GAR_DECEASED_PTIA,
    GAR_REDUC,
    GAR_RCONJ,
    GAR_INCAP,
    GAR_INVAL
  } = guarantees;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <WidePageLayout>
          <Icon name="circleUmbrella" size={70}/>
          <PageTitle className="mb-8">
            {getMiddleText('NIVEAUXGARANTIESETTARIFS_GENERAL_VIEW_TITLE')}
          </PageTitle>
          <PageSubtitle className="mb-8">
            {getMiddleText('NIVEAUXGARANTIESETTARIFS_GENERAL_VIEW_DESCRIPTION')}
          </PageSubtitle>

          {(getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_EST_NECESSAIRE') &&
            includes('BROKER', usersInformation[0].roles)) && (
            <ControlledCommissionCard
              form={form}
              title={getMiddleText('NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_TITLE')}
              infoMessage={getMiddleText('NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_MESSAGE_INFO')}
              radioOptions={getCommissionOptions(getMiddleText('NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_VALEUR'))}
              selectOptions={getCommissionRateOptions(getMiddleText('NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_VALEUR'))}
              selectLabel={getMiddleText('NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_LISTE_LABEL')}
              commissions={commissions}
              rules={isRequiredSelect(
                getCommissionOptions(getMiddleText('NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_VALEUR')),
                getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_EST_NECESSAIRE')
              )}
              data-testid="COMMISSION"
            />
          )}

          <SimulationPricingsSelfWorkerExtraRecommendationMessages
            recommendationMessages={recommendationMessages}
            form={form}
            guarantees={guarantees}
            getMiddleBoolean={getMiddleBoolean}
          />

          {getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_DECES_EST_NECESSAIRE') && (
            <SectionCard
              icon="umbrella"
              title={getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_LABEL')}>
              {GAR_DECEASED_PTIA &&
                getMiddleBoolean(
                  'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_EST_NECESSAIRE'
                ) && (
                <NiveauxGarantiesTarifsPrevTnsGarantiesDecesForm
                  form={form}
                  guarantees={guarantees}
                  getMiddleText={getMiddleText}
                  getMiddleBoolean={getMiddleBoolean}
                  annualIncome={annualIncome}
                  pricesLoading={pricesLoading}
                />
              )}

              {GAR_REDUC &&
                getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_DECES_REDUC_EST_NECESSAIRE') && (
                <NiveauxGarantiesTarifsPrevTnsRenteEducationForm
                  form={form}
                  guarantees={guarantees}
                  getMiddleText={getMiddleText}
                  getMiddleNumber={getMiddleNumber}
                  getMiddleBoolean={getMiddleBoolean}
                  minBirthDateReducChild={minBirthDateReducChild}
                  maxBirthDateReducChild={maxBirthDateReducChild}
                  minBirthDateHospiChild={minBirthDateHospiChild}
                  maxBirthDateHospiChild={maxBirthDateHospiChild}
                  annualIncome={annualIncome}
                  pricesLoading={pricesLoading}
                />
              )}
              {GAR_RCONJ &&
                getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_DECES_RCONJ_EST_NECESSAIRE') && (
                <NiveauxGarantiesTarifsPrevTnsRenteConjointForm
                  form={form}
                  guarantees={guarantees}
                  getMiddleText={getMiddleText}
                  getMiddleNumber={getMiddleNumber}
                  getMiddleBoolean={getMiddleBoolean}
                  annualIncome={annualIncome}
                  pricesLoading={pricesLoading}
                />
              )}
              {!isValid && map(
                ({
                  fieldName,
                  message
                }) =>
                  path(fieldName.split('.'), form.formState.errors) && (
                    <Message className="w-full" key={fieldName} variant="error" label={message}/>
                  )
              )(decesErrors)}
            </SectionCard>
          )}

          {getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_INVAL_EST_NECESSAIRE') &&
            (GAR_INCAP || GAR_INVAL) && (
            <SectionCard
              icon="crutches"
              title={getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_INVAL_LABEL')}>
              {GAR_INCAP && <>
                {getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_EST_NECESSAIRE') && (
                  <NiveauxGarantiesTarifsPrevTnsIncapaciteTemporaireDeTravailForm
                    form={form}
                    guarantees={guarantees}
                    pricesGuarantees={pricesGuarantees}
                    getMiddleText={getMiddleText}
                    getMiddleBoolean={getMiddleBoolean}
                    getMiddleNumber={getMiddleNumber}
                    minBirthDateReducChild={minBirthDateReducChild}
                    maxBirthDateReducChild={maxBirthDateReducChild}
                    minBirthDateHospiChild={minBirthDateHospiChild}
                    maxBirthDateHospiChild={maxBirthDateHospiChild}
                    onOptionFrMalChange={onOptionFrMalChange}
                    onOptionFrAccChange={onOptionFrAccChange}
                    onOptionFrHospChange={onOptionFrHospChange}
                    annualIncome={annualIncome}
                    PASS={PASS}
                    registerComputedFranchiseDataGetter={registerComputedFranchiseDataGetter}
                    pricesLoading={pricesLoading}
                  />
                )}
                {!isValid && map(
                  ({
                    fieldName,
                    message
                  }) =>
                    path(fieldName.split('.'), form.formState.errors) && (
                      <Message className="w-full" key={fieldName} variant="error" label={message}/>
                    )
                )(incapErrors)}
              </>}
              {GAR_INVAL && <>
                {getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_EST_NECESSAIRE') && (
                  <NiveauxGarantiesTarifsPrevTnsInvaliditeForm
                    form={form}
                    guarantees={guarantees}
                    getMiddleText={getMiddleText}
                    getMiddleBoolean={getMiddleBoolean}
                    getMiddleNumber={getMiddleNumber}
                    customerCSPCategory={customerCSPCategory}
                    onInvalDepCapFocus={handleInvalDepCapFocus}
                    onInvalDepCapBlur={handleInvalDepCapBlur}
                    annualIncome={annualIncome}
                    pricesLoading={pricesLoading}
                  />
                )}
                {!isValid && map(
                  ({
                    fieldName,
                    message
                  }) =>
                    path(fieldName.split('.'), form.formState.errors) && (
                      <Message className="w-full" key={fieldName} variant="error" label={message}/>
                    )
                )(invalErrors)}
              </>}
            </SectionCard>
          )}
          {displayPriceError && (
            <Message
              className="w-full"
              key={'priceError'}
              variant="error"
              label={
                'Un problème est survenu lors du calcul du tarif. Veuillez réessayer ultérieurement.'
              }
            />
          )}
        </WidePageLayout>

        <ButtonContainer
          $onPreviousButtonClick={$onClickPrev}
          getMiddleText={getMiddleText}
          nextButtonLabel={getMiddleText('GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL')}
          displayResumeButton
          nextButtonIsDisabled={!isValid || pricesLoading || isEmpty(pricesGuarantees) || displayPriceError}
        />
      </form>
    </FormProvider>
  );
};

export default SimulationPricingsSelfWorkerForm;
